import { useEffect, useState } from "react";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { Audit } from "@/services/api/guardiansv2/types";
import { formatEntityTypeLabel } from "@/components/missions/entityTypeDisplay";
import { useOsNavigate } from "@/components/ui/os-layout";
import { ContextRow } from "../shared/ContextRow";
import { ReadableDataPanel } from "@/components/ui/readable-content";
import { getAuditedEntityLabel, getAuditedEntityRowLabel } from "./auditEntityLabel";
import { entityDeepLink, entityRefFromAudit } from "../shared/entityDeepLink";
import { guardiansRulesUrl, guardiansShareUrl } from "@/navigation/apps/guardiansLocation";
import { isIssueFirstAudit } from "./auditPresentation";
import type { ScopeLabelCache } from "./useScopeLabelCache";

export interface AuditInspectorContextProps {
  audit: Audit;
  scopeLabels: ScopeLabelCache;
}

function shortId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

/** Standardised scope + entity panel for the audit inspector sidebar. */
export function AuditInspectorContext({
  audit,
  scopeLabels,
}: AuditInspectorContextProps) {
  const osNavigate = useOsNavigate();
  const { scope } = audit;

  const [clientName, setClientName] = useState<string | null>(null);
  const [clientLoading, setClientLoading] = useState(false);
  const missionId = scope.mission_id;
  const taskId = scope.task_id;
  const interactionId = scope.interaction_id;
  const entityType = scope.entity_type;
  const entityId = scope.entity_id;

  useEffect(() => {
    if (entityType !== "fa_client" || !entityId) {
      setClientName(null);
      return;
    }
    let mounted = true;
    setClientLoading(true);
    AgenticV2API.getClient(entityId)
      .then((client) => {
        if (mounted) setClientName(client.display_name || null);
      })
      .catch(() => {
        if (mounted) setClientName(null);
      })
      .finally(() => {
        if (mounted) setClientLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [entityType, entityId]);

  const missionText =
    scopeLabels.missionLabel(missionId) ??
    (missionId ? `Mission ${shortId(missionId)}` : null);
  const taskText =
    scopeLabels.taskLabel(taskId) ?? (taskId ? `Task ${shortId(taskId)}` : null);
  const subjectId = String(audit.subject.id);
  const scopeMissionIsSubject =
    audit.subject.type === "mission" &&
    missionId != null &&
    missionId === subjectId;
  const scopeTaskIsSubject =
    audit.subject.type === "task" && taskId != null && taskId === subjectId;
  const scopeInteractionIsSubject =
    audit.subject.type === "interaction" &&
    interactionId != null &&
    interactionId === subjectId;
  const interactionText =
    interactionId && !scopeInteractionIsSubject
      ? `Interaction ${shortId(interactionId)}`
      : null;

  const clientTypeLabel = entityType ? formatEntityTypeLabel(entityType) : "";
  const clientText =
    clientName ??
    (clientLoading
      ? "Loading…"
      : entityId
        ? `${clientTypeLabel} ${shortId(entityId)}`
        : null);

  const entityRef = entityRefFromAudit(audit);
  const entityLink = entityRef ? entityDeepLink(entityRef) : null;
  const missionLink = missionId
    ? entityDeepLink({ kind: "mission", id: missionId, scope: { mission_id: missionId } })
    : null;
  const taskLink = taskId
    ? entityDeepLink({
        kind: "task",
        id: taskId,
        scope: { mission_id: missionId, task_id: taskId },
      })
    : null;
  const interactionLink = interactionId
    ? entityDeepLink({
        kind: "interaction",
        id: interactionId,
        scope: {
          mission_id: missionId,
          task_id: taskId,
          interaction_id: interactionId,
        },
      })
    : null;
  const entityLabel = getAuditedEntityLabel(audit, scopeLabels);
  const entityRowLabel = getAuditedEntityRowLabel(audit);
  const issueFirst = isIssueFirstAudit(audit);
  const snapshot = audit.screening_snapshot;
  const alertHref = audit.origin_alert_id
    ? guardiansShareUrl({ alert: audit.origin_alert_id })
    : null;
  const rulesHref =
    snapshot?.catalogue_key != null
      ? guardiansRulesUrl({ catalogueKey: snapshot.catalogue_key })
      : null;

  const auditContent = audit.inputs?.content ?? null;

  return (
    <section className="space-y-3" data-testid="audit-inspector-context">
      {issueFirst && alertHref ? (
        <ContextRow
          label="Source alert"
          onClick={() => osNavigate(alertHref)}
          data-testid="audit-context-origin-alert"
        >
          {snapshot?.alert_title ?? "View alert"}
        </ContextRow>
      ) : null}
      {issueFirst && rulesHref && snapshot?.catalogue_key ? (
        <ContextRow
          label="Rules"
          onClick={() => osNavigate(rulesHref)}
          data-testid="audit-context-rules"
        >
          {snapshot.catalogue_key}
        </ContextRow>
      ) : null}
      {missionText && !scopeMissionIsSubject ? (
        <ContextRow
          label="Mission"
          onClick={missionLink ? () => missionLink.run({ osNavigate }) : undefined}
          data-testid="audit-context-mission"
        >
          {missionText}
        </ContextRow>
      ) : null}
      {taskText && !scopeTaskIsSubject ? (
        <ContextRow
          label="Task"
          onClick={taskLink ? () => taskLink.run({ osNavigate }) : undefined}
          data-testid="audit-context-task"
        >
          {taskText}
        </ContextRow>
      ) : null}
      {interactionText ? (
        <ContextRow
          label="Interaction"
          onClick={
            interactionLink ? () => interactionLink.run({ osNavigate }) : undefined
          }
          data-testid="audit-context-interaction"
        >
          {interactionText}
        </ContextRow>
      ) : null}
      {entityType && entityId && clientText ? (
        <ContextRow
          label={clientTypeLabel || "Client"}
          onClick={
            entityType === "fa_client"
              ? () => osNavigate(`/clients/${entityId}`)
              : undefined
          }
          data-testid="audit-context-client"
        >
          {clientText}
        </ContextRow>
      ) : null}
      <ContextRow
        label={entityRowLabel}
        onClick={entityLink ? () => entityLink.run({ osNavigate }) : undefined}
        data-testid="audit-context-entity"
      >
        {entityLabel}
      </ContextRow>
      {audit.inputs?.step_count != null ? (
        <ContextRow label="Steps">{audit.inputs.step_count}</ContextRow>
      ) : null}
      {audit.inputs?.tool_names?.length ? (
        <ContextRow label="Tools">{audit.inputs.tool_names.join(", ")}</ContextRow>
      ) : null}
      {auditContent ? (
        <ContextRow label="Audit context">
          <ReadableDataPanel title="Audit context" content={auditContent} />
        </ContextRow>
      ) : null}
    </section>
  );
}
